/**
 * In-memory advisory serviceability result for the current restaurant/destination.
 * Source of truth for coordinates remains the destination store.
 */

import { getDestinationCoordinates } from "@/domain/destination/destination-coordinates.store"
import type { DestinationCoordinates } from "@/domain/destination/destination-coordinates.types"
import type { ServiceabilityState } from "@/domain/serviceability/serviceability-state.types"

export type { ServiceabilityState }

type InternalServiceability = {
  kind: Exclude<ServiceabilityState["kind"], "idle">
  restaurantId: string
  /** Exact restaurantId|lat|lng of the evaluated pin, or null when checked with no pin. */
  fingerprint: string | null
}

let nextRequestId = 0
let activeRequestId = 0
let current: InternalServiceability | null = null

export function destinationServiceabilityFingerprint(
  coords: DestinationCoordinates
): string {
  return `${coords.restaurantId}|${coords.latitude}|${coords.longitude}`
}

export function beginServiceabilityRequest(): number {
  const requestId = ++nextRequestId
  activeRequestId = requestId
  return requestId
}

export function isCurrentServiceabilityRequest(requestId: number): boolean {
  return requestId === activeRequestId && requestId !== 0
}

export function abandonPendingServiceabilityChecks(): void {
  activeRequestId = 0
}

export function setServiceabilityChecking(
  restaurantId: string,
  fingerprint: string | null
): void {
  current = {
    kind: "checking",
    restaurantId,
    fingerprint,
  }
}

export function setServiceabilityTerminal(
  requestId: number,
  kind: Exclude<ServiceabilityState["kind"], "idle" | "checking">,
  restaurantId: string,
  fingerprint: string | null
): void {
  if (!isCurrentServiceabilityRequest(requestId)) return
  current = { kind, restaurantId, fingerprint }
}

export function resetServiceabilityState(): void {
  current = null
  activeRequestId = 0
}

export function resetServiceabilityStoreForTests(): void {
  resetServiceabilityState()
  nextRequestId = 0
}

/**
 * Visible state for `restaurantId`. A result is shown only when it belongs to
 * that restaurant and still matches the current destination pin.
 */
export function getServiceabilityState(restaurantId: string): ServiceabilityState {
  const rid = restaurantId.trim()
  if (!rid || !current || current.restaurantId !== rid) {
    return { kind: "idle" }
  }

  const coords = getDestinationCoordinates(rid)
  if (current.fingerprint == null) {
    if (current.kind === "invalid_destination" && !coords) {
      return { kind: "invalid_destination" }
    }
    return { kind: "idle" }
  }
  if (!coords) {
    return { kind: "idle" }
  }
  if (current.fingerprint !== destinationServiceabilityFingerprint(coords)) {
    return { kind: "idle" }
  }
  return { kind: current.kind }
}
